import React from "react";
import { useTranslation } from "react-i18next";
import {
    Actions,
    Badge,
    Box,
    Button,
    Dialog,
    Drawer,
    Dropdown,
    IconAdd,
    IconDelete,
    IconEdit,
    IconStar,
    Inline,
    Notice,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    TextField,
    TooltipRenderer,
} from "braid-design-system";
import { useToast } from "../../hooks/useToast";
import { ItemLotResponse } from "../../services/itemLotsService";
import {
    createItemResource,
    updateItemResource,
    deleteItemResource,
    uploadResourceFile,
    getResourcesByItem,
    setPrimaryItemResource,
    clearPrimaryItemResource,
} from "../../services/itemResourceService";
import { toStaticResourceUrl } from "../../utils/resourceUrl";

const MAX_IMAGES = 6;
const MAX_VIDEOS = 1;

interface ResourceFormData {
    resource_type: "image" | "video";
    resource_path: string;
    resource_sort_order: string;
    resource_title: string;
    selectedFile?: File | null;
}

const defaultFormData: ResourceFormData = {
    resource_type: "image",
    resource_path: "",
    resource_sort_order: "",
    resource_title: "",
    selectedFile: null,
};

interface EditableResource extends ResourceFormData {
    localId: string;
    resource_id?: number | null;
    resource_itemlots_code: string;
    is_primary?: number | null;
    isNew?: boolean;
}

interface CreateUpdateItemResourceDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    selectedItemLot?: ItemLotResponse | null;
    onItemResourceSaved?: () => void;
    isReadOnly?: boolean;
}

const CreateUpdateItemResourceDrawer = ({
    isOpen,
    onClose,
    selectedItemLot,
    onItemResourceSaved,
    isReadOnly = false,
}: CreateUpdateItemResourceDrawerProps) => {
    const { t } = useTranslation();
    const toast = useToast();

    const [resources, setResources] = React.useState<EditableResource[]>([]);
    const [loading, setLoading] = React.useState(false);

    // Dialog state
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editingResource, setEditingResource] = React.useState<EditableResource | null>(null);
    const [formData, setFormData] = React.useState<ResourceFormData>(defaultFormData);
    const [saving, setSaving] = React.useState(false);

    // Fetch existing resources when drawer opens for a selected item
    React.useEffect(() => {
        if (!isOpen || !selectedItemLot?.itemlots_code) {
            setResources([]);
            return;
        }

        const fetchExistingResources = async () => {
            try {
                setLoading(true);
                const data = await getResourcesByItem(selectedItemLot.itemlots_code!);
                const mapped: EditableResource[] = data.map((r) => ({
                    localId: `existing-${r.resource_id ?? Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    resource_id: r.resource_id,
                    resource_itemlots_code: r.resource_itemlots_code ?? selectedItemLot.itemlots_code!,
                    resource_type: (r.resource_type as "image" | "video") || "image",
                    resource_path: r.resource_path ?? "",
                    resource_sort_order: r.resource_sort_order != null ? String(r.resource_sort_order) : "",
                    resource_title: r.resource_title ?? "",
                    is_primary: r.is_primary,
                    selectedFile: null,
                    isNew: false,
                }));
                setResources(mapped);
            } catch (error: any) {
                toast.showError(`Error loading resources: ${error?.message || "Unknown error"}`);
                setResources([]);
            } finally {
                setLoading(false);
            }
        };

        fetchExistingResources();
    }, [isOpen, selectedItemLot?.itemlots_code, toast]);

    // Open dialog for adding a new resource
    const handleOpenAddDialog = () => {
        setEditingResource(null);
        setFormData({
            resource_type: "image",
            resource_path: "",
            resource_sort_order: "",
            resource_title: "",
            selectedFile: null,
        });
        setDialogOpen(true);
    };

    // Open dialog for editing an existing resource
    const handleOpenEditDialog = (resource: EditableResource) => {
        setEditingResource(resource);
        setFormData({
            resource_type: resource.resource_type,
            resource_path: resource.resource_path,
            resource_sort_order: resource.resource_sort_order,
            resource_title: resource.resource_title,
            selectedFile: null,
        });
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingResource(null);
        setFormData(defaultFormData);
    };

    const updateFormField = (field: keyof ResourceFormData, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleFileSelect = (file: File | null) => {
        setFormData((prev) => ({
            ...prev,
            selectedFile: file,
            resource_path: file ? file.name : prev.resource_path,
        }));
    };

    // Save resource from dialog (create or update)
    const handleSaveFromDialog = async () => {
        if (!selectedItemLot?.itemlots_code) return;

        if (!(formData.resource_path ?? "").trim() && !formData.selectedFile) {
            toast.showError("Please select a file to upload");
            return;
        }

        // If editing and resource_type changed, user must upload a new file
        if (
            editingResource &&
            editingResource.resource_type !== formData.resource_type &&
            !formData.selectedFile
        ) {
            toast.showError(
                `Resource type changed from "${editingResource.resource_type}" to "${formData.resource_type}". Please upload a new ${formData.resource_type} file.`,
            );
            return;
        }

        try {
            setSaving(true);

            let finalPath = formData.resource_path;
            if (formData.selectedFile) {
                finalPath = await uploadResourceFile(
                    formData.selectedFile,
                    formData.resource_type,
                    selectedItemLot.itemlots_code,
                );
            }

            const payload = {
                resource_itemlots_code: selectedItemLot.itemlots_code,
                resource_type: formData.resource_type,
                resource_path: finalPath,
                resource_sort_order: formData.resource_sort_order === "" ? null : Number(formData.resource_sort_order),
                resource_title: formData.resource_title === "" ? null : formData.resource_title,
            };

            if (editingResource?.resource_id) {
                await updateItemResource(editingResource.resource_id, payload);
                toast.showSuccess("Resource updated successfully");
            } else {
                await createItemResource(payload);
                toast.showSuccess("Resource created successfully");
            }

            // Refresh the list for this item
            if (selectedItemLot?.itemlots_code) {
                const data = await getResourcesByItem(selectedItemLot.itemlots_code);
                const mapped: EditableResource[] = data.map((r) => ({
                    localId: `existing-${r.resource_id ?? Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    resource_id: r.resource_id,
                    resource_itemlots_code: r.resource_itemlots_code ?? selectedItemLot.itemlots_code!,
                    resource_type: (r.resource_type as "image" | "video") || "image",
                    resource_path: r.resource_path ?? "",
                    resource_sort_order: r.resource_sort_order != null ? String(r.resource_sort_order) : "",
                    resource_title: r.resource_title ?? "",
                    is_primary: r.is_primary,
                    selectedFile: null,
                    isNew: false,
                }));
                setResources(mapped);
            }

            handleCloseDialog();

            if (onItemResourceSaved) onItemResourceSaved();
        } catch (error: any) {
            toast.showError(error?.message || "Failed to save resource");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteResource = async (localId: string) => {
        const resource = resources.find((r) => r.localId === localId);
        if (!resource) return;

        if (!resource.resource_id) return;

        if (!window.confirm("Are you sure you want to delete this resource?")) {
            return;
        }

        try {
            await deleteItemResource(resource.resource_id);
            toast.showSuccess("Resource deleted successfully");
            setResources((prev) => prev.filter((r) => r.localId !== localId));
            if (onItemResourceSaved) onItemResourceSaved();
        } catch (error: any) {
            toast.showError(`Error deleting resource: ${error?.message || "Unknown error"}`);
        }
    };

    const handleSetPrimary = async (resource: EditableResource) => {
        if (!resource.resource_id) return;

        try {
            await setPrimaryItemResource(resource.resource_id);
            toast.showSuccess("Main image updated successfully");
            setResources((prev) =>
                prev.map((r) => ({
                    ...r,
                    is_primary: r.resource_type === "image" ? (r.resource_id === resource.resource_id ? 1 : 0) : r.is_primary,
                })),
            );
            if (onItemResourceSaved) onItemResourceSaved();
        } catch (error: any) {
            toast.showError(`Error setting main image: ${error?.message || "Unknown error"}`);
        }
    };

    const handleClearPrimary = async (resource: EditableResource) => {
        if (!resource.resource_id) return;

        try {
            await clearPrimaryItemResource(resource.resource_id);
            toast.showSuccess("Main image unset successfully");
            setResources((prev) =>
                prev.map((r) => (r.resource_id === resource.resource_id ? { ...r, is_primary: 0 } : r)),
            );
            if (onItemResourceSaved) onItemResourceSaved();
        } catch (error: any) {
            toast.showError(`Error unsetting main image: ${error?.message || "Unknown error"}`);
        }
    };

    const imageCount = resources.filter((r) => r.resource_type === "image").length;
    const videoCount = resources.filter((r) => r.resource_type === "video").length;

    const getResourceTypeCountsText = () => {
        const parts: string[] = [];
        parts.push(`${imageCount}/${MAX_IMAGES} images`);
        parts.push(`${videoCount}/${MAX_VIDEOS} video`);
        return parts.join(" \u00B7 ");
    };

    return (
        <React.Fragment>
            <Drawer
                open={isOpen}
                onClose={onClose}
                title={
                    selectedItemLot?.itemlots_code
                        ? `Resources for ${selectedItemLot.itemlots_code} (${selectedItemLot.itemlots_desc})`
                        : t("page.item_resource_management.drawer.title.default", { defaultValue: "Item Resources" })
                }
                width="large"
            >
                <Box padding="large">
                    <Stack space="large">
                        <Notice tone="info">
                            <Text>
                                {t("page.item_resource_management.drawer.limitation", {
                                    defaultValue: `Maximum ${MAX_IMAGES} images and ${MAX_VIDEOS} video per item.`,
                                })}
                                {` ${getResourceTypeCountsText()}`}
                            </Text>
                        </Notice>

                        {loading ? (
                            <Text tone="secondary">
                                {t("page.item_resource_management.drawer.loading", { defaultValue: "Loading resources..." })}
                            </Text>
                        ) : null}

                        {!loading && resources.length > 0 ? (
                            <Table
                                label={t("page.item_resource_management.drawer.table.label", {
                                    defaultValue: "Resources List",
                                })}
                            >
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCell>
                                            <Text>{t("page.item_resource_management.drawer.table.headers.preview", { defaultValue: "Preview" })}</Text>
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            <Text>{t("page.item_resource_management.drawer.table.headers.type", { defaultValue: "Type" })}</Text>
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            <Text>{t("page.item_resource_management.drawer.table.headers.file", { defaultValue: "File" })}</Text>
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            <Text>{t("page.item_resource_management.drawer.table.headers.title", { defaultValue: "Title" })}</Text>
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            <Text>{t("page.item_resource_management.drawer.table.headers.sort_order", { defaultValue: "Sort Order" })}</Text>
                                        </TableHeaderCell>
                                        <TableHeaderCell align="center">
                                            <Text>{t("page.item_resource_management.drawer.table.headers.main", { defaultValue: "Main" })}</Text>
                                        </TableHeaderCell>
                                        <TableHeaderCell align="right">
                                            <Text>{t("page.item_resource_management.drawer.table.headers.actions", { defaultValue: "Actions" })}</Text>
                                        </TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {resources.map((resource) => {
                                        const previewUrl = toStaticResourceUrl(resource.resource_path);
                                        const isPrimary = resource.resource_type === "image" && resource.is_primary === 1;
                                        return (
                                        <TableRow key={resource.localId}>
                                            <TableCell>
                                                {previewUrl && resource.resource_type === "image" ? (
                                                    <Box
                                                        background="neutralLight"
                                                        borderRadius="standard"
                                                        style={{ width: 60, height: 45, overflow: "hidden" }}
                                                    >
                                                        <img
                                                            src={previewUrl}
                                                            alt={resource.resource_title || "preview"}
                                                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                                        />
                                                    </Box>
                                                ) : (
                                                    <Text tone="secondary" size="small">
                                                        {resource.resource_type === "video" ? "Video" : "-"}
                                                    </Text>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Text>{resource.resource_type === "image" ? "Image" : "Video"}</Text>
                                            </TableCell>
                                            <TableCell>
                                                <Text>
                                                    {resource.resource_path
                                                        ? resource.resource_path.split("/").pop()
                                                        : t("page.item_resource_management.list.table.na", { defaultValue: "N/A" })}
                                                </Text>
                                            </TableCell>
                                            <TableCell>
                                                <Text>{resource.resource_title || "-"}</Text>
                                            </TableCell>
                                            <TableCell>
                                                <Text>{resource.resource_sort_order || "-"}</Text>
                                            </TableCell>
                                            <TableCell align="center">
                                                {resource.resource_type === "image" ? (
                                                    <Badge tone={isPrimary ? "positive" : "neutral"}>{isPrimary ? "Main" : "-"}</Badge>
                                                ) : (
                                                    <Text tone="secondary">-</Text>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {!isReadOnly ? (
                                                    <Inline space="small">
                                                        {resource.resource_type === "image" ? (
                                                            <TooltipRenderer
                                                                tooltip={<Text>{isPrimary
                                                                    ? t("page.item_resource_management.drawer.tooltip.unset_main", { defaultValue: "Unset as main image" })
                                                                    : t("page.item_resource_management.drawer.tooltip.set_main", { defaultValue: "Set as main image" })}</Text>}
                                                            >
                                                                {({ triggerProps }) => (
                                                                    <Box
                                                                        onClick={() => (isPrimary ? handleClearPrimary(resource) : handleSetPrimary(resource))}
                                                                        cursor="pointer"
                                                                        {...triggerProps}
                                                                    >
                                                                        <IconStar tone={isPrimary ? "formAccent" : "secondary"} />
                                                                    </Box>
                                                                )}
                                                            </TooltipRenderer>
                                                        ) : null}
                                                        <TooltipRenderer
                                                            tooltip={<Text>{t("page.item_resource_management.drawer.tooltip.edit", { defaultValue: "Edit" })}</Text>}
                                                        >
                                                            {({ triggerProps }) => (
                                                                <Box
                                                                    onClick={() => handleOpenEditDialog(resource)}
                                                                    cursor="pointer"
                                                                    {...triggerProps}
                                                                >
                                                                    <IconEdit tone="info" />
                                                                </Box>
                                                            )}
                                                        </TooltipRenderer>
                                                        <TooltipRenderer
                                                            tooltip={<Text>{t("page.item_resource_management.drawer.tooltip.delete", { defaultValue: "Delete" })}</Text>}
                                                        >
                                                            {({ triggerProps }) => (
                                                                <Box
                                                                    onClick={() => handleDeleteResource(resource.localId)}
                                                                    cursor="pointer"
                                                                    {...triggerProps}
                                                                >
                                                                    <IconDelete tone="critical" />
                                                                </Box>
                                                            )}
                                                        </TooltipRenderer>
                                                    </Inline>
                                                ) : null}
                                            </TableCell>
                                        </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        ) : null}

                        {!loading && resources.length === 0 && selectedItemLot?.itemlots_code ? (
                            <Text tone="secondary">
                                {t("page.item_resource_management.drawer.empty", {
                                    defaultValue: "No resources available. Click 'Add Resource' to add one.",
                                })}
                            </Text>
                        ) : null}

                        {!isReadOnly && selectedItemLot?.itemlots_code ? (
                            <Actions>
                                <Button
                                    variant="ghost"
                                    tone="brandAccent"
                                    icon={<IconAdd />}
                                    onClick={handleOpenAddDialog}
                                >
                                    {t("page.item_resource_management.drawer.button.add_resource", {
                                        defaultValue: "Add Resource",
                                    })}
                                </Button>
                            </Actions>
                        ) : null}
                    </Stack>
                </Box>
            </Drawer>

            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                title={
                    editingResource
                        ? t("page.item_resource_management.dialog.title.edit", { defaultValue: "Edit Resource" })
                        : t("page.item_resource_management.dialog.title.add", { defaultValue: "Add Resource" })
                }
                width="medium"
            >
                <Box padding="large">
                    <Stack space="large">
                        <Dropdown
                            label={t("page.item_resource_management.dialog.label.type", { defaultValue: "Resource Type" })}
                            value={formData.resource_type}
                            onChange={(e) => updateFormField("resource_type", e.currentTarget.value)}
                        >
                            <option value="image">Image</option>
                            <option value="video">Video</option>
                        </Dropdown>

                        <Box>
                            <Text size="standard" weight="strong">
                                {t("page.item_resource_management.dialog.label.file", { defaultValue: "Upload File" })}
                            </Text>
                            <Box marginTop="xsmall">
                                <input
                                    type="file"
                                    accept={formData.resource_type === "image" ? "image/*" : "video/*"}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0] || null;
                                        handleFileSelect(file);
                                    }}
                                    style={{
                                        display: "block",
                                        width: "100%",
                                        padding: "8px 12px",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                        fontSize: "14px",
                                    }}
                                />
                            </Box>
                            {formData.resource_path && !formData.selectedFile && (
                                <Box marginTop="xsmall">
                                    <Text size="small" tone="secondary">
                                        {t("page.item_resource_management.dialog.label.current_file", {
                                            defaultValue: "Current file: ",
                                        })}
                                        {formData.resource_path.split("/").pop()}
                                    </Text>
                                </Box>
                            )}
                        </Box>

                        <TextField
                            label={t("page.item_resource_management.dialog.label.title", { defaultValue: "Title" })}
                            placeholder="Optional title"
                            value={formData.resource_title}
                            onChange={(e) => updateFormField("resource_title", e.currentTarget.value)}
                        />

                        <TextField
                            label={t("page.item_resource_management.dialog.label.sort_order", { defaultValue: "Sort Order" })}
                            placeholder="e.g. 1"
                            value={formData.resource_sort_order}
                            onChange={(e) => {
                                const sanitized = e.currentTarget.value.replace(/[^\d]/g, "");
                                updateFormField("resource_sort_order", sanitized);
                            }}
                        />

                        <Actions>
                            <Button
                                variant="ghost"
                                onClick={handleCloseDialog}
                            >
                                {t("page.item_resource_management.dialog.button.cancel", { defaultValue: "Cancel" })}
                            </Button>
                            <Button
                                variant="solid"
                                tone="brandAccent"
                                loading={saving}
                                onClick={handleSaveFromDialog}
                            >
                                {editingResource
                                    ? t("page.item_resource_management.dialog.button.update", { defaultValue: "Update" })
                                    : t("page.item_resource_management.dialog.button.save", { defaultValue: "Save" })}
                            </Button>
                        </Actions>
                    </Stack>
                </Box>
            </Dialog>
        </React.Fragment>
    );
};

export default CreateUpdateItemResourceDrawer;